/*
 * 模块: CaseTab
 * 职责: 渲染实战案例标签页（包含数据表格和对比案例）
 * 依赖: antd组件、类型定义、FormattedText组件
 * 导出: CaseTab组件
 * 限制: 文件<280行, 函数<30行
 */

import React from 'react';
import { Card, Space, Alert, Typography } from 'antd';
import { BulbOutlined } from '@ant-design/icons';
import type { MetricDefinition } from '../../../types';
import FormattedText from '../FormattedText';

const { Text, Paragraph } = Typography;

interface CaseTabProps {
  definition: MetricDefinition;
}

const renderDataTable = (data: any[]) => {
  const hasRankColumns = data[0].rank1 !== undefined;

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ background: '#f0f0f0', borderBottom: '2px solid #d9d9d9' }}>
          <th style={{ padding: '12px', textAlign: 'left' }}>周期</th>
          {hasRankColumns && (
            <React.Fragment key="rank-headers">
              <th style={{ padding: '12px', textAlign: 'right' }}>排名#1</th>
              <th style={{ padding: '12px', textAlign: 'right' }}>排名#2</th>
              <th style={{ padding: '12px', textAlign: 'right' }}>排名#3</th>
            </React.Fragment>
          )}
          <th style={{ padding: '12px', textAlign: 'right' }}>Top3总份额</th>
        </tr>
      </thead>
      <tbody>
        {data.map((row, idx) => (
          <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
            <td style={{ padding: '12px', fontWeight: 'bold' }}>{row.week}</td>
            {row.rank1 !== undefined && (
              <React.Fragment key={`rank-${idx}`}>
                <td style={{ padding: '12px', textAlign: 'right' }}>{row.rank1}%</td>
                <td style={{ padding: '12px', textAlign: 'right' }}>{row.rank2}%</td>
                <td style={{ padding: '12px', textAlign: 'right' }}>{row.rank3}%</td>
              </React.Fragment>
            )}
            <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', color: '#1890ff' }}>
              {row.total}%
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const renderComparison = (comparison: any) => {
  return (
    <Card 
      size="small" 
      title="🔄 对比案例" 
      style={{ borderColor: '#ff7875', background: '#fff1f0' }}
    >
      <Alert
        message={comparison?.scenario || ''}
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
      />
      
      <Card size="small" style={{ background: '#fffbe6', marginBottom: 16 }}>
        <FormattedText text={comparison?.calculation || ''} />
      </Card>

      <Alert
        message="深度洞察"
        description={<FormattedText text={comparison?.insight || ''} />}
        type="info"
        showIcon
        icon={<BulbOutlined />}
      />
    </Card>
  );
};

const renderRealCase = (realCase: any) => {
  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Alert
        message={
          <Text strong style={{ fontSize: '16px' }}>
            {realCase.title}
          </Text>
        }
        description={realCase.scenario}
        type="success"
        showIcon
        icon={<BulbOutlined />}
        style={{ background: '#f6ffed' }}
      />
      
      {/* 原始数据表格 */}
      <Card size="small" title="📊 原始数据" style={{ background: '#fafafa' }}>
        {renderDataTable(realCase.data)}
      </Card>

      {/* 对比案例 */}
      {realCase.comparison && renderComparison(realCase.comparison)}
    </Space>
  );
};

const renderSimpleExample = (example: any) => {
  return (
    <Card size="small">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div style={{ textAlign: 'left' }}>
          <Text strong style={{ color: '#1890ff', fontSize: '15px' }}>场景描述</Text>
          <Paragraph style={{ marginTop: 8, marginBottom: 0, textAlign: 'left' }}>
            {example.scenario}
          </Paragraph>
        </div>
        <div style={{ textAlign: 'left' }}>
          <Text strong style={{ color: '#52c41a', fontSize: '15px' }}>计算过程</Text>
          <div className="calculation-result">
            <pre style={{ textAlign: 'left' }}>{example.calculation}</pre>
          </div>
        </div>
        <div style={{ textAlign: 'left' }}>
          <Text strong style={{ color: '#fa8c16', fontSize: '15px' }}>结果解读</Text>
          <Paragraph style={{ marginTop: 8, marginBottom: 0, textAlign: 'left' }}>
            {example.interpretation}
          </Paragraph>
        </div>
      </Space>
    </Card>
  );
};

export const CaseTab: React.FC<CaseTabProps> = ({ definition }) => {
  if (definition.real_case) {
    return renderRealCase(definition.real_case);
  }

  if (definition.example) {
    return renderSimpleExample(definition.example);
  }

  return (
    <Alert
      message="暂无实战案例"
      description="该指标的实战案例正在补充中..."
      type="info"
      showIcon
    />
  );
};

